import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { theme } from '../theme';

const FooterContainer = styled.footer`
  width: 100%;
  background: #FFFFFF;
  box-shadow: 0px -4px 9.9px 1px rgba(0, 0, 0, 0.08);
  padding: 24px 0 24px 0;
  box-sizing: border-box;
  position: relative;
  margin-top: 0px;

  @media (max-width: 768px) {
    padding: 20px 0 20px 0;
    margin-top: 0px;
  }

  @media (max-width: 480px) {
    padding: 16px 0 16px 0;
    margin-top: 0px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 5%;
    right: 5%;
    height: 1px;
    background: #283275;
  }

  &::after {
    content: '';
    position: absolute;
    top: -12px;
    left: 0;
    right: 0;
    height: 12px;
    background: #FFFFFF;
    z-index: -1;

    @media (max-width: 768px) {
      top: -8px;
      height: 8px;
    }

    @media (max-width: 480px) {
      top: -6px;
      height: 6px;
    }
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 48px;
  padding: 8px 2% 0 2%;
  align-items: flex-start;

  @media (max-width: 768px) {
    gap: 32px;
    padding: 6px 2% 0 2%;
  }

  @media (max-width: 480px) {
    gap: 24px;
    padding: 4px 2% 0 2%;
  }
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  text-align: center;
`;

const FooterLogo = styled.div<{ $imageUrl: string }>`
  width: 280px;
  height: 100px;
  background: url(${props => props.$imageUrl});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  flex-shrink: 0;
  margin: 0 auto;

  @media (max-width: 768px) {
    width: 240px;
    height: 86px;
  }

  @media (max-width: 480px) {
    width: 200px;
    height: 72px;
  }
`;

const SocialIconsContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const SocialIconLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease, opacity 0.3s ease;
  text-decoration: none;

  &:hover {
    transform: scale(1.1);
    opacity: 0.8;
  }

  img {
    width: 32px;
    height: 32px;
    object-fit: contain;

    @media (max-width: 480px) {
      width: 28px;
      height: 28px;
    }
  }
`;

const FooterLinksContainer = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const FooterLink = styled(Link)`
  font-family: ${theme.fonts.body};
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.colors.outchou_blue};
  text-decoration: none;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const FooterMenuButton = styled.button`
  font-family: ${theme.fonts.body};
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.colors.outchou_blue};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const ShareTextContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  font-family: ${theme.fonts.body};
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #000000;
  position: relative;

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const ShareLink = styled.button`
  font-family: ${theme.fonts.body};
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.colors.outchou_blue};
  background: none;
  border: none;
  padding: 0;
  margin-left: 4px;
  cursor: pointer;
  text-decoration: underline;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const Notification = styled.div<{ $show: boolean }>`
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%) translateY(${props => props.$show ? '0' : '20px'});
  background: ${theme.colors.outchou_blue};
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-family: ${theme.fonts.body};
  font-size: 14px;
  font-weight: 500;
  opacity: ${props => props.$show ? 1 : 0};
  pointer-events: ${props => props.$show ? 'auto' : 'none'};
  transition: opacity 0.3s ease, transform 0.3s ease;
  z-index: 10000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  @media (max-width: 480px) {
    bottom: 80px;
    padding: 10px 20px;
    font-size: 12px;
  }
`;

const Copyright = styled.div`
  font-family: ${theme.fonts.body};
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #000000;

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const Footer: React.FC = () => {
  const [showNotification, setShowNotification] = useState(false);
  
  const socialLinks = {
    instagram: 'https://www.instagram.com/_outchou_/',
    tiktok: 'https://www.tiktok.com/@_outchou_',
  };

  const handleMenuClick = () => {
    window.dispatchEvent(new CustomEvent('openMobileMenu'));
  };

  const handleShareClick = async () => {
    const siteUrl = window.location.origin;
    try {
      await navigator.clipboard.writeText(siteUrl);
      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
      }, 2000);
    } catch (err) {
      // Fallback pour les navigateurs qui ne supportent pas l'API Clipboard
      const textArea = document.createElement('textarea');
      textArea.value = siteUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setShowNotification(true);
        setTimeout(() => {
          setShowNotification(false);
        }, 2000);
      } catch (err) {
        console.error('Erreur lors de la copie:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterLogo $imageUrl="/shared_assets/images/Logo%20site.jpeg"/>
        <SocialIconsContainer>
          <SocialIconLink 
            href={socialLinks.instagram} 
            target="_blank" 
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <img src="/shared_assets/images/icons/instagram.jpg" alt="Instagram" />
          </SocialIconLink>
          <SocialIconLink 
            href={socialLinks.tiktok} 
            target="_blank" 
            rel="noopener noreferrer"
            aria-label="TikTok"
          >
            <img src="/shared_assets/images/icons/tik tok.png" alt="TikTok" />
          </SocialIconLink>
        </SocialIconsContainer>
        <FooterLinksContainer>
          <FooterLink to="/about">A propos</FooterLink>
          <FooterLink to="/contact">Contact</FooterLink>
          <FooterMenuButton onClick={handleMenuClick}>Menu</FooterMenuButton>
        </FooterLinksContainer>
        <ShareTextContainer>
          Vous aimez Outchou ? <ShareLink onClick={handleShareClick}>Partagez-le.</ShareLink>
        </ShareTextContainer>
        <FooterBottom>
          <LeftColumn>
            <Copyright>© 2025 OUTCHOU. Tous droits réservés.</Copyright>
          </LeftColumn>
        </FooterBottom>
      </FooterContent>
      <Notification $show={showNotification}>Lien copié</Notification>
    </FooterContainer>
  );
};

export default Footer;
